import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { bandPosition, businessDate, joiningDateProblem, type OfferDetailView, type OfferTermsRequest } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field, FieldRow } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { wholeNumber } from '../../../lib/whole-number';
import { BandNote, money } from '../offer-format';
import { digitsOnly, draftOf, grossOf, requestOf, toBig, type DraftTerms } from '../offer-terms';
import styles from './Offers.module.css';

/** A pause in typing this long saves the offer (the job wizard's rhythm). */
const AUTOSAVE_MS = 800;

/**
 * OFR-01 — the builder (F13.01). Amounts are typed in whole taka (or pounds) and stored in
 * minor units (#7). It saves itself; the gross figure and where it sits against the band are
 * worked out as you type (S23 Q7), so an offer outside the band is never a surprise.
 */
export function OfferTermsForm({ offer, onSave }: { readonly offer: OfferDetailView; readonly onSave: (terms: OfferTermsRequest) => Promise<boolean> }): ReactNode {
  const [draft, setDraft] = useState<DraftTerms>(() => draftOf(offer));
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const latest = useRef(offer);
  const timer = useRef<number | null>(null);

  // A save reads the newest version the server returned, never the one this form opened on.
  useEffect(() => {
    latest.current = offer;
  }, [offer]);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  function change(patch: Partial<DraftTerms>): void {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save(next), AUTOSAVE_MS);
  }

  async function save(terms: DraftTerms): Promise<void> {
    timer.current = null;
    const joiningProblem = terms.joiningDate ? joiningDateProblem(terms.joiningDate, businessDate(new Date())) : null;
    if (joiningProblem) return;
    setState('saving');
    const isSaved = await onSave(requestOf(terms, latest.current.version));
    setState(isSaved ? 'saved' : 'idle');
  }

  const currency = offer.offer.currency ?? 'BDT';
  const grossMinor = grossOf(draft);
  const band = grossMinor === null ? null : bandPosition(grossMinor, toBig(offer.offer.bandMinMinor), toBig(offer.offer.bandMaxMinor));
  const joiningProblem = draft.joiningDate ? joiningDateProblem(draft.joiningDate, businessDate(new Date())) : null;

  return (
    <Card title="The offer" meta={state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : 'Monthly figures · saved as you type'}>
      <div className={styles.form}>
        {offer.status === 'approved' && <p className={styles.notice}>This offer is approved. Changing anything sends it back for approval, and a new letter is made.</p>}
        {offer.status === 'rejected' && offer.statusReason && <p className={styles.warning}>Sent back: “{offer.statusReason}”. Change it, then send it for approval again.</p>}
        {offer.status === 'negotiating' && <p className={styles.notice}>Version {offer.currentVersion + 1}. Inside the band it goes straight to {offer.candidateName}; outside the band it goes for approval first.</p>}

        <Field label="Basic salary" isRequired hint="Monthly gross basic, whole amounts.">
          {({ inputId }) => <Input id={inputId} isNumeric inputMode="numeric" prefix={currency} value={draft.base} placeholder="45000" onChange={(event) => change({ base: digitsOnly(event.target.value) })} />}
        </Field>

        <div className={styles.allowances}>
          <span className={styles.subhead}>Allowances</span>
          {draft.allowances.length === 0 && <p className={styles.quiet}>None. Add house rent, transport or anything else paid each month.</p>}
          {draft.allowances.map((line, index) => (
            <div key={index} className={styles.allowanceRow}>
              <Input aria-label={`Allowance ${index + 1} name`} value={line.label} placeholder="House rent" onChange={(event) => change({ allowances: draft.allowances.map((item, i) => (i === index ? { ...item, label: event.target.value } : item)) })} />
              <Input aria-label={`Allowance ${index + 1} amount`} isNumeric inputMode="numeric" prefix={currency} value={line.amount} placeholder="6000" onChange={(event) => change({ allowances: draft.allowances.map((item, i) => (i === index ? { ...item, amount: digitsOnly(event.target.value) } : item)) })} />
              <Button size="sm" variant="ghost" icon={Trash2} aria-label={`Remove allowance ${index + 1}`} onClick={() => change({ allowances: draft.allowances.filter((_, i) => i !== index) })} />
            </div>
          ))}
          <Button size="sm" variant="ghost" icon={Plus} onClick={() => setDraft({ ...draft, allowances: [...draft.allowances, { label: '', amount: '' }] })}>
            Add an allowance
          </Button>
        </div>

        <div className={styles.grossLine}>
          <span className={styles.subhead}>Gross monthly</span>
          <span className={styles.gross}>{grossMinor === null ? '—' : money(grossMinor.toString(), currency)}</span>
          <BandNote position={band} minMinor={offer.offer.bandMinMinor} maxMinor={offer.offer.bandMaxMinor} currency={currency} isEnforced={offer.isBandEnforced} />
        </div>

        <FieldRow>
          <Field label="Joining date" isRequired {...(joiningProblem ? { error: joiningProblem } : {})}>
            {({ inputId, isInvalid }) => <DatePicker id={inputId} label="Joining date" isInvalid={isInvalid} min={businessDate(new Date())} value={draft.joiningDate} onChange={(joiningDate) => change({ joiningDate })} timeZone="Asia/Dhaka" />}
          </Field>
          <Field label="Work location">
            {({ inputId }) => <Input id={inputId} value={draft.workLocation} placeholder="Head office, Dhaka" onChange={(event) => change({ workLocation: event.target.value })} />}
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Offer stays open for">
            {({ inputId }) => <Input id={inputId} isNumeric inputMode="numeric" suffix="days" value={String(draft.validityDays)} onChange={(event) => change({ validityDays: wholeNumber(event.target.value) })} />}
          </Field>
          <Field label="Probation">
            {({ inputId }) => <Input id={inputId} isNumeric inputMode="numeric" suffix="months" value={String(draft.probationMonths)} onChange={(event) => change({ probationMonths: wholeNumber(event.target.value) })} />}
          </Field>
          <Field label="Notice period">
            {({ inputId }) => <Input id={inputId} value={draft.noticePeriod} placeholder="1 month" onChange={(event) => change({ noticePeriod: event.target.value })} />}
          </Field>
        </FieldRow>

        {offer.isInternalApplicant && (
          <div>
            <Checkbox checked={draft.isCurrentManagerTold} label="Their current manager has been told about this offer" onChange={(isCurrentManagerTold) => change({ isCurrentManagerTold })} />
            <p className={styles.hint}>An internal applicant's manager hears before the offer is sent (§L15).</p>
          </div>
        )}
      </div>
    </Card>
  );
}
